import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Download, Eye, Play, Share2, FileText, AlertCircle, ChevronDown, ChevronUp, Copy, Check } from 'lucide-react';
import {
  apiFetch,
  classifyReportAccessStatus,
  readApiErrorMessage,
  type ReportAccessState,
} from '../utils/apiClient';
import SignInWithTwitchButton from './SignInWithTwitchButton';
import {
  ReportConfidenceBanner,
  resolveOverallConfidenceTier,
} from './ReportConfidenceBanner';
import ReportLimitedSampleNotice from './ReportLimitedSampleNotice';
import ReportAnalysisQualityNotice, {
  type AnalysisQuality,
} from './ReportAnalysisQualityNotice';
import ScoutingContextBanner, { type ScoutingContext } from './ScoutingContextBanner';
import CoachLayerSection from './report/CoachLayerSection';
import MotionReviewNotesSection, {
  hasRenderableMotionReviewNotes,
} from './report/MotionReviewNotesSection';
import CoachReportSection from './report/CoachReportSection';
import { isMotionReviewNotesEnabled } from '../config/featureFlags';
import { useUser } from '../contexts/UserContext';
import {
  hasRenderableScoutingReportV2,
  parseScoutingReportV2,
  resolveReportMotionReviewNotes,
  type MotionReviewNotesPayload,
  type ScoutingReportV2,
} from '../lib/reportApi';
import type { CoachLayer } from '../types/coachLayer';
import type { CategoryConfidence } from '../utils/customerReportLabels';

interface DetectionEntry {
  timestamp?: number;
  formation?: string;
  play_type?: string;
  down_distance?: string;
  confidence?: number;
}

interface DetailedAnalysis {
  formations_detected?: DetectionEntry[];
  play_types?: DetectionEntry[];
  down_distance_plays?: DetectionEntry[];
}

interface VisionSampling {
  total_extracted_frames?: number;
  merged_candidate_frames?: number;
  analyzed_frames?: number;
  vision_frame_cap?: number;
  skipped_frames?: number;
  estimated_duration_sec?: number;
}

interface ReportData {
  analysisId: string;
  scoutingSummary: string;
  offensiveAnalysis?: any;
  defensiveAnalysis?: any;
  playArtAnalysis?: any;
  teamScoutingReport?: {
    team: string;
    category_confidence?: CategoryConfidence;
    offense: {
      offense_snaps?: number;
      team_match_rate?: number;
      run_pass_ratio: { pass: number; run: number };
      run_calls?: number;
      pass_calls?: number;
      avg_yards: { pass: number; run: number };
      top_formations: string[];
      top_personnel: string[];
      motion_uses: number;
      play_action_uses: number;
      rpo_uses: number;
      pass_depth_mix: { short: number; intermediate: number; deep: number; null: number };
      situational: {
        by_down: { [key: string]: { pass: number; run: number } };
        red_zone?: number | { pass: number; run: number };
        two_minute?: number | { pass: number; run: number };
      };
      situational_intelligence?: SituationalCoachingSections;
      third_down?: {
        plays: number;
        conversions: number;
        conversion_rate?: number;
        unknown?: number;
        known_attempts?: number;
        unknown_attempts?: number;
        excluded_attempts?: number;
        result_inference?: {
          source?: string;
          confidence?: string;
          sample_warning?: string | null;
        };
      };
      strengths: string[];
      weaknesses: string[];
      summary: string;
    };
    defense: {
      defense_snaps?: number;
      top_coverages: string[];
      top_fronts: string[];
      blitz_rate: number;
      coverage_by_situation?: CoverageBySituationIntel;
      third_down: {
        plays: number;
        conversions: number;
        conversion_rate?: number;
        stops?: number;
        stop_rate?: number;
        unknown?: number;
        known_attempts?: number;
        unknown_attempts?: number;
        excluded_attempts?: number;
        result_inference?: {
          source?: string;
          confidence?: string;
          sample_warning?: string | null;
        };
      };
      pressure_profile_reliable?: boolean;
      avg_rushers?: number | null;
      fourth_down: { plays: number; conversions: number };
      strengths: string[];
      weaknesses: string[];
      summary: string;
    };
  };
  detailedAnalysis: DetailedAnalysis | null;
  isCompleted: boolean;
  stats: {
    visionAnalyzedFrames: number;
    framesExtracted: number;
    framesSkipped: number;
    visionDetections: number;
  };
  visionSampling: VisionSampling | null;
  categoryConfidence?: CategoryConfidence | null;
  gameplanSnapshot?: GameplanSnapshot | null;
  coachLayer?: CoachLayer | null;
  analysisQuality?: AnalysisQuality | null;
  scoutingContext?: ScoutingContext | null;
  scoutingReportV2?: ScoutingReportV2 | null;
}

type ConfidenceTier = 'High' | 'Moderate' | 'Limited';

interface SituationRate {
  value?: number;
  insufficient_sample?: boolean;
  sample_size?: number;
}

interface SituationBucketView {
  label?: string;
  plays?: number;
  run?: number;
  pass?: number;
  run_rate?: number | SituationRate;
  pass_rate?: number | SituationRate;
  pass_rate_percent?: number;
  run_rate_percent?: number;
  top_formations?: { name: string; count: number }[];
  top_coverages?: {
    name: string;
    count: number;
    share?: number;
    share_percent?: number;
  }[];
  coverage_sample_size?: number;
  coverage_insufficient_sample?: boolean;
  coverage_evidence?: string[];
  situation_confidence?: string;
  confidence?: string;
  confidence_helper?: string;
  predictability?: string;
  narrative?: string;
  tendency?: string;
  evidence?: string[];
  narrative_eligible?: boolean;
}

interface CoverageSituationSection {
  situation_key?: string;
  label?: string;
  top_coverage?: string;
  share_percent?: number;
  coverage_sample_size?: number;
  coverage_predictability?: string;
  confidence?: string;
  confidence_helper?: string;
  insufficient_sample?: boolean;
  message?: string;
  evidence?: string[];
  narrative?: string;
}

interface CoverageBySituationIntel {
  sections?: CoverageSituationSection[];
  most_predictable_coverage_situation?: CoverageSituationSection & { narrative?: string };
  coaching_insights?: string[];
}

interface MostPredictableSituation {
  label?: string;
  situation_key?: string;
  pass_rate?: number;
  run_rate?: number;
  plays?: number;
  confidence?: string;
  confidence_helper?: string;
  predictability?: string;
  tendency?: string;
  narrative?: string;
  evidence?: string[];
  empty_state?: boolean;
  message?: string;
}

interface SituationalCoachingSections {
  third_down_analysis?: Record<string, SituationBucketView>;
  red_zone_analysis?: SituationBucketView;
  two_minute_analysis?: SituationBucketView;
  fourth_down_analysis?: {
    attempts?: number;
    rollup?: SituationBucketView;
    breakdown?: Record<string, SituationBucketView>;
  };
  most_predictable_situation?: MostPredictableSituation | null;
  offensive_insights?: string[];
  defensive_insights?: string[];
  predictability?: {
    high_leverage_situations?: MostPredictableSituation[];
    high_leverage_message?: string | null;
  };
}

interface GameplanEvidence {
  timestamp?: number;
  label?: string;
  situation?: string;
  play_type?: string;
  down_distance?: string;
  team?: string;
}

interface GameplanSnapshotCard {
  type?: string;
  title?: string;
  headline?: string;
  summary?: string;
  coaching_point?: string;
  confidence?: string;
  sample_size?: number;
  sample_context?: string;
  why_it_matters?: string;
  what_to_watch?: string;
  adjustment_trigger?: string;
  conversion_summary?: string;
  unknown_outcomes?: number;
  watchouts?: string[];
  evidence?: Array<string | GameplanEvidence>;
}

interface GameplanSnapshot {
  enabled?: boolean;
  status?: 'ready' | 'limited_sample' | 'unavailable';
  headline?: string;
  confidence?: string;
  sample_context?: string;
  cards?: GameplanSnapshotCard[];
  coach_notes?: string[];
  watchouts?: string[];
}

function confidenceBadgeClass(confidence?: string): string {
  if (confidence === 'High') {
    return 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200';
  }
  if (confidence === 'Medium') {
    return 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200';
  }
  return 'bg-secondary-100 text-secondary-700 dark:bg-secondary-800 dark:text-secondary-300';
}

function formatEvidenceLabel(item: string | GameplanEvidence): string {
  if (typeof item === 'string') {
    return item;
  }
  if (item.label) {
    return item.label;
  }
  if (item.timestamp != null) {
    const mins = Math.floor(item.timestamp / 60);
    const secs = Math.floor(item.timestamp % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }
  return 'Evidence';
}

function GameplanSnapshotSection({ snapshot }: { snapshot?: GameplanSnapshot | null }) {
  if (!snapshot?.enabled) return null;

  const cards = snapshot.cards || [];
  const watchouts = snapshot.watchouts || [];
  const showLimitedFallback =
    snapshot.status === 'unavailable' && cards.length === 0;

  return (
    <div className="card border-2 border-primary-200 dark:border-primary-700">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
        <div>
          <h2 className="text-2xl font-bold text-secondary-900 dark:text-white">
            Gameplan Snapshot
          </h2>
          {snapshot.sample_context && (
            <p className="text-sm text-secondary-500 dark:text-secondary-400 mt-1">
              {snapshot.sample_context}
            </p>
          )}
        </div>
        {snapshot.confidence && (
          <span
            className={`inline-flex self-start px-3 py-1 rounded-full text-xs font-semibold ${confidenceBadgeClass(snapshot.confidence)}`}
          >
            {snapshot.confidence} confidence
          </span>
        )}
      </div>

      {snapshot.headline && (
        <p className="text-secondary-800 dark:text-secondary-200 font-medium mb-5 leading-relaxed">
          {snapshot.headline}
        </p>
      )}

      {showLimitedFallback ? (
        <p className="text-secondary-600 dark:text-secondary-300 text-sm leading-relaxed">
          Early Read: Sample is still limited. Use the report below as a film-study starter, not a
          full tendency profile.
        </p>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {cards.map((card, index) => (
            <div
              key={`${card.type || 'card'}-${index}`}
              className="bg-secondary-50 dark:bg-secondary-800/80 rounded-lg p-4 border border-secondary-200 dark:border-secondary-700"
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <h3 className="font-semibold text-secondary-900 dark:text-white">{card.title}</h3>
                {card.confidence && (
                  <span
                    className={`shrink-0 px-2 py-0.5 rounded text-xs font-medium ${confidenceBadgeClass(card.confidence)}`}
                  >
                    {card.confidence}
                  </span>
                )}
              </div>
              {card.headline && (
                <p className="text-sm font-semibold text-secondary-800 dark:text-secondary-200 mb-2">
                  {card.headline}
                </p>
              )}
              {card.conversion_summary && (
                <p className="text-sm font-medium text-primary-700 dark:text-primary-300 mb-2">
                  {card.conversion_summary}
                  {card.unknown_outcomes != null && card.unknown_outcomes > 0 && (
                    <span className="text-secondary-500 dark:text-secondary-400 font-normal">
                      {' '}
                      · {card.unknown_outcomes} unknown excluded
                    </span>
                  )}
                </p>
              )}
              {card.sample_context && (
                <p className="text-xs text-secondary-500 dark:text-secondary-400 mb-2">
                  {card.sample_context}
                </p>
              )}
              <p className="text-sm text-secondary-700 dark:text-secondary-300 mb-3 leading-relaxed">
                {card.summary}
              </p>
              {card.why_it_matters && (
                <p className="text-sm text-secondary-700 dark:text-secondary-300 mb-2">
                  <span className="font-medium text-secondary-900 dark:text-white">Why it matters: </span>
                  {card.why_it_matters}
                </p>
              )}
              {card.what_to_watch && (
                <p className="text-sm text-secondary-700 dark:text-secondary-300 mb-2">
                  <span className="font-medium text-secondary-900 dark:text-white">What to watch: </span>
                  {card.what_to_watch}
                </p>
              )}
              {card.adjustment_trigger && (
                <p className="text-sm text-secondary-700 dark:text-secondary-300 mb-2">
                  <span className="font-medium text-secondary-900 dark:text-white">Adjustment trigger: </span>
                  {card.adjustment_trigger}
                </p>
              )}
              {card.coaching_point && (
                <p className="text-sm text-primary-700 dark:text-primary-300 font-medium mb-2">
                  {card.coaching_point}
                </p>
              )}
              {(card.watchouts || []).length > 0 && (
                <ul className="mb-2 space-y-1 text-xs text-amber-800 dark:text-amber-200">
                  {(card.watchouts || []).map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              )}
              <div className="flex flex-wrap gap-2 text-xs text-secondary-500 dark:text-secondary-400">
                {card.sample_size != null && card.sample_size > 0 && (
                  <span>{card.sample_size} snaps</span>
                )}
                {(card.evidence || []).map((stamp, stampIndex) => (
                  <span
                    key={`${card.type || 'card'}-evidence-${stampIndex}`}
                    className="px-2 py-0.5 rounded bg-white dark:bg-secondary-900 border border-secondary-200 dark:border-secondary-600"
                  >
                    {formatEvidenceLabel(stamp)}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {(snapshot.coach_notes || []).length > 0 && (
        <div className="mt-5">
          <h4 className="text-sm font-semibold text-secondary-900 dark:text-white mb-2">
            Coach Notes
          </h4>
          <ul className="list-disc list-inside space-y-1 text-sm text-secondary-600 dark:text-secondary-300">
            {(snapshot.coach_notes || []).map((note) => (
              <li key={note}>{note}</li>
            ))}
          </ul>
        </div>
      )}

      {watchouts.length > 0 && (
        <div className="mt-5 pt-4 border-t border-secondary-200 dark:border-secondary-700">
          <h4 className="text-sm font-semibold text-secondary-900 dark:text-white mb-2">
            Sample Watchouts
          </h4>
          <ul className="space-y-2">
            {watchouts.map((item) => (
              <li
                key={item}
                className="text-sm text-amber-800 dark:text-amber-200 bg-amber-50 dark:bg-amber-900/20 rounded-md px-3 py-2"
              >
                {item}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function confidenceHelperLine(confidence?: string, helper?: string): string | null {
  if (helper) return helper;
  if (confidence === 'High') return 'Supported by a strong sample size.';
  if (confidence === 'Medium') return 'Supported by a moderate sample size.';
  if (confidence === 'Low' || confidence === 'Limited') return 'Additional observations recommended.';
  return null;
}

const EVIDENCE_PREVIEW_LIMIT = 25;

function formatCoverageName(name: string): string {
  return name.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatSituationRate(
  rate: number | SituationRate | undefined | null
): string {
  if (rate == null) return 'Insufficient sample';
  if (typeof rate === 'object') {
    if (rate.insufficient_sample) {
      const n = rate.sample_size ?? 0;
      return `Insufficient sample (${n} snaps)`;
    }
    if (rate.value != null) return `${(rate.value * 100).toFixed(0)}%`;
    return 'Insufficient sample';
  }
  return `${(rate * 100).toFixed(0)}%`;
}

function SituationBucketCard({
  title,
  bucket,
}: {
  title: string;
  bucket: SituationBucketView;
}) {
  if (!bucket?.plays) return null;
  return (
    <div className="bg-secondary-50 dark:bg-secondary-800 p-4 rounded-lg">
      <div className="flex justify-between items-start mb-2">
        <h4 className="font-medium text-secondary-900 dark:text-white">{title}</h4>
        {bucket.situation_confidence && (
          <span className="text-xs text-secondary-500">{bucket.situation_confidence} confidence</span>
        )}
      </div>
      <p className="text-sm text-secondary-600 dark:text-secondary-300 mb-2">
        {bucket.plays} snaps · Pass{' '}
        {bucket.pass_rate_percent != null
          ? `${bucket.pass_rate_percent}%`
          : formatSituationRate(bucket.pass_rate)}{' '}
        · Run{' '}
        {bucket.run_rate_percent != null
          ? `${bucket.run_rate_percent}%`
          : formatSituationRate(bucket.run_rate)}
      </p>
      {bucket.predictability && (
        <p className="text-xs text-secondary-500 mb-1">
          Predictability: {bucket.predictability}
          {(bucket.confidence || bucket.situation_confidence) &&
            ` · Confidence: ${bucket.confidence || bucket.situation_confidence}`}
        </p>
      )}
      {confidenceHelperLine(bucket.confidence || bucket.situation_confidence, bucket.confidence_helper) && (
        <p className="text-xs text-secondary-400 mb-1">
          {confidenceHelperLine(bucket.confidence || bucket.situation_confidence, bucket.confidence_helper)}
        </p>
      )}
      {bucket.narrative && (
        <p className="text-sm text-secondary-600 dark:text-secondary-300 italic">{bucket.narrative}</p>
      )}
      {bucket.top_formations && bucket.top_formations.length > 0 && (
        <p className="text-xs text-secondary-500">
          Top formations:{' '}
          {bucket.top_formations.map((f) => `${f.name} (${f.count})`).join(', ')}
        </p>
      )}
      {bucket.top_coverages && bucket.top_coverages.length > 0 && (
        <p className="text-xs text-secondary-500">
          Top coverages:{' '}
          {bucket.top_coverages
            .map((c) =>
              c.share_percent != null
                ? `${formatCoverageName(c.name)} (${c.share_percent}%)`
                : `${formatCoverageName(c.name)} (${c.count})`
            )
            .join(', ')}
        </p>
      )}
      {bucket.coverage_evidence && bucket.coverage_evidence.length > 0 && (
        <p className="text-xs text-secondary-500 mt-1">
          Coverage evidence: {bucket.coverage_evidence.slice(0, 5).join(', ')}
        </p>
      )}
      {bucket.evidence && bucket.evidence.length > 0 && (
        <p className="text-xs text-secondary-500 mt-1">
          Evidence: {bucket.evidence.slice(0, 5).join(', ')}
        </p>
      )}
    </div>
  );
}
const MIN_OFFENSE_SNAPS_EVIDENCE = 8;
const MIN_RUN_PASS_TAGS_EVIDENCE = 5;

function getOffenseSnaps(offense?: {
  offense_snaps?: number;
  run_calls?: number;
  pass_calls?: number;
} | null): number {
  if (!offense) return 0;
  if (typeof offense.offense_snaps === 'number') return offense.offense_snaps;
  return (offense.run_calls ?? 0) + (offense.pass_calls ?? 0);
}

function hasRunPassEvidence(offense?: {
  offense_snaps?: number;
  run_calls?: number;
  pass_calls?: number;
} | null): boolean {
  if (!offense) return false;
  const snaps = getOffenseSnaps(offense);
  const tags = (offense.run_calls ?? 0) + (offense.pass_calls ?? 0);
  return snaps >= MIN_OFFENSE_SNAPS_EVIDENCE && tags >= MIN_RUN_PASS_TAGS_EVIDENCE;
}

function resolveVisionStats(fullData: any): {
  visionAnalyzedFrames: number;
  framesExtracted: number;
  framesSkipped: number;
  visionSampling: VisionSampling | null;
} {
  const visionSampling: VisionSampling | null = fullData.video_analysis?.vision_sampling ?? null;
  const analyzed =
    visionSampling?.analyzed_frames ??
    fullData.video_analysis?.vision_analyzed_frames ??
    0;
  const extracted =
    visionSampling?.total_extracted_frames ?? fullData.video_analysis?.frames_analyzed ?? analyzed;
  const skipped = visionSampling?.skipped_frames ?? Math.max(0, extracted - analyzed);

  return {
    visionAnalyzedFrames: analyzed,
    framesExtracted: extracted,
    framesSkipped: skipped,
    visionSampling,
  };
}

function formatTimestamp(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  const mins = Math.floor(s / 60);
  const secs = s % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

function thirdDownKnownAttempts(stats?: {
  plays?: number;
  known_attempts?: number;
}): number {
  if (!stats) return 0;
  return stats.known_attempts ?? stats.plays ?? 0;
}

function thirdDownUnknownAttempts(stats?: {
  unknown?: number;
  unknown_attempts?: number;
}): number {
  if (!stats) return 0;
  return stats.unknown_attempts ?? stats.unknown ?? 0;
}

function getConfidenceTierFromBackend(categoryConfidence?: CategoryConfidence | null): ConfidenceTier | null {
  const overall = categoryConfidence?.overall_confidence;
  if (overall === 'High') return 'High';
  if (overall === 'Moderate') return 'Moderate';
  if (overall === 'Limited' || overall === 'Low') return 'Limited';
  return null;
}

function getConfidenceTier(visionAnalyzedFrames: number, offenseSnaps: number): ConfidenceTier {
  if (offenseSnaps >= 25 && visionAnalyzedFrames >= 40) return 'High';
  if (offenseSnaps >= 10 || visionAnalyzedFrames >= 20) return 'Moderate';
  return 'Limited';
}

function offenseTabTitle(context?: ScoutingContext | null, fallbackTeam?: string): string {
  const confident = context?.confidence === 'high' || context?.confidence === 'medium';
  const label = context?.target_team || (confident ? fallbackTeam : undefined);
  if (label) return `${label} Offensive Analysis`;
  if (context?.target_type === 'both') return 'Offensive Analysis (Both Teams)';
  return 'Detected Offensive Tendencies';
}

function defenseTabTitle(context?: ScoutingContext | null, fallbackTeam?: string): string {
  const confident = context?.confidence === 'high' || context?.confidence === 'medium';
  const label = context?.target_team || (confident ? fallbackTeam : undefined);
  if (label) return `${label} Defensive Analysis`;
  if (context?.target_type === 'both') return 'Defensive Analysis (Both Teams)';
  return 'Detected Defensive Tendencies';
}

function buildReportFromFull(fullData: any, analysisId: string): ReportData {
  const offense = fullData.team_scouting_report?.offense;
  const visionStats = resolveVisionStats(fullData);

  return {
    analysisId,
    scoutingSummary:
      offense?.summary ||
      fullData.offensive_analysis?.summary ||
      'Analysis completed successfully!',
    offensiveAnalysis: fullData.offensive_analysis || null,
    defensiveAnalysis: fullData.defensive_analysis || null,
    playArtAnalysis: fullData.play_art_analysis || null,
    teamScoutingReport: fullData.team_scouting_report || null,
    detailedAnalysis: fullData.detailed_analysis || null,
    isCompleted: true,
    stats: {
      visionAnalyzedFrames: visionStats.visionAnalyzedFrames,
      framesExtracted: visionStats.framesExtracted,
      framesSkipped: visionStats.framesSkipped,
      visionDetections: fullData.plays_detected || 0,
    },
    visionSampling: visionStats.visionSampling,
    categoryConfidence:
      fullData.confidence ?? fullData.team_scouting_report?.category_confidence ?? null,
    gameplanSnapshot: fullData.gameplan_snapshot ?? null,
    coachLayer: fullData.coach_layer ?? null,
    analysisQuality: fullData.analysis_quality ?? null,
    scoutingContext: fullData.scouting_context ?? null,
    scoutingReportV2: parseScoutingReportV2(fullData.scouting_report_v2),
  };
}

function buildReportFromStatus(statusData: any, analysisId: string): ReportData {
  const results = statusData.analysis_results || {};
  const offense = results.team_scouting_report?.offense;
  const visionSampling: VisionSampling | null = results.vision_sampling ?? null;
  const analyzed = visionSampling?.analyzed_frames ?? results.frames_analyzed ?? 0;
  const extracted = visionSampling?.total_extracted_frames ?? results.frames_analyzed ?? analyzed;
  const skipped = visionSampling?.skipped_frames ?? Math.max(0, extracted - analyzed);

  return {
    analysisId,
    scoutingSummary:
      offense?.summary ||
      results.scouting_summary ||
      'Analysis completed successfully!',
    offensiveAnalysis: results.offensive_analysis || null,
    defensiveAnalysis: results.defensive_analysis || null,
    playArtAnalysis: results.play_art_analysis || null,
    teamScoutingReport: results.team_scouting_report || null,
    detailedAnalysis: null,
    isCompleted: true,
    stats: {
      visionAnalyzedFrames: analyzed,
      framesExtracted: extracted,
      framesSkipped: skipped,
      visionDetections: results.plays_detected || results.stats?.plays_detected || 0,
    },
    visionSampling,
    categoryConfidence: results.confidence ?? results.team_scouting_report?.category_confidence ?? null,
    gameplanSnapshot: results.gameplan_snapshot ?? null,
    coachLayer: results.coach_layer ?? null,
    analysisQuality: results.analysis_quality ?? null,
    scoutingContext: results.scouting_context ?? null,
    scoutingReportV2: parseScoutingReportV2(results.scouting_report_v2),
  };
}

const Report: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [activeTab, setActiveTab] = useState<'summary' | 'offensive' | 'defensive'>('summary');
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [accessState, setAccessState] = useState<ReportAccessState>(null);
  const [evidenceOpen, setEvidenceOpen] = useState(true);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
  const [reportIdCopied, setReportIdCopied] = useState(false);

  const [progressStatus, setProgressStatus] = useState<{
    status: string;
    progress: number;
    currentStep: number;
    message: string;
  } | null>(null);
  const [motionReviewNotes, setMotionReviewNotes] = useState<MotionReviewNotesPayload | null>(
    null
  );
  const [motionReviewNotesLoading, setMotionReviewNotesLoading] = useState(false);
  const { loading: authLoading } = useUser();

  const analysisId = id;

  const handleAccessDenied = async (response: Response): Promise<boolean> => {
    const access = classifyReportAccessStatus(response.status);
    if (!access) return false;
    const message = (await readApiErrorMessage(response)) || '';
    setAccessState(access);
    if (access === 'forbidden') {
      setError(message || 'This report belongs to another account.');
    } else if (access === 'not_found') {
      setError(message || 'Report not found.');
    } else {
      setError(message || 'Please sign in to view this report.');
    }
    return true;
  };

  useEffect(() => {
    if (!analysisId) {
      navigate('/upload');
      return;
    }

    let cancelled = false;
    let pollTimer: ReturnType<typeof setInterval> | null = null;

    const fetchAnalysisResults = async (): Promise<string | null> => {
      try {
        const statusResponse = await apiFetch(`/api/status/${analysisId}`);

        if (!statusResponse.ok) {
          if (await handleAccessDenied(statusResponse)) {
            return 'denied';
          }
          throw new Error(`Failed to fetch status: ${statusResponse.status}`);
        }

        const statusData = await statusResponse.json();
        if (cancelled) return null;

        setProgressStatus({
          status: statusData.status,
          progress: statusData.progress ?? 0,
          currentStep: statusData.currentStep ?? 0,
          message: statusData.message ?? '',
        });

        if (statusData.status === 'completed') {
          try {
            const fullResponse = await apiFetch(`/api/analysis/${analysisId}/full`);

            if (!fullResponse.ok && (await handleAccessDenied(fullResponse))) {
              return 'denied';
            }

            if (fullResponse.ok) {
              const fullData = await fullResponse.json();
              setReportData(buildReportFromFull(fullData, analysisId));
            } else {
              setReportData(buildReportFromStatus(statusData, analysisId));
            }
          } catch {
            setReportData(buildReportFromStatus(statusData, analysisId));
          }
        } else if (statusData.status === 'error') {
          setReportData({
            analysisId,
            scoutingSummary:
              statusData.error_message ||
              statusData.message ||
              'An error occurred during analysis.',
            detailedAnalysis: null,
            isCompleted: false,
            stats: {
              visionAnalyzedFrames: 0,
              framesExtracted: 0,
              framesSkipped: 0,
              visionDetections: 0,
            },
            visionSampling: null,
            ...(statusData.error_message ? { status: 'error', error_message: statusData.error_message } : {}),
          } as ReportData & { status?: string; error_message?: string });
        } else {
          setReportData((prev) => ({
            analysisId,
            scoutingSummary:
              statusData.message ||
              'Your video is being analyzed. We are reviewing key moments from your gameplay to build your scouting report.\n\nThis page refreshes automatically every 5 seconds.',
            detailedAnalysis: null,
            isCompleted: false,
            stats: prev?.stats ?? {
              visionAnalyzedFrames: 0,
              framesExtracted: 0,
              framesSkipped: 0,
              visionDetections: 0,
            },
            visionSampling: prev?.visionSampling ?? null,
          }));
        }

        return statusData.status as string;
      } catch (fetchError) {
        console.error('Error fetching analysis results:', fetchError);
        if (!cancelled) {
          setError('Failed to load analysis results');
        }
        return null;
      }
    };

    const startPolling = async () => {
      const initialStatus = await fetchAnalysisResults();
      if (cancelled) return;
      setLoading(false);

      if (
        initialStatus &&
        initialStatus !== 'completed' &&
        initialStatus !== 'error' &&
        initialStatus !== 'denied'
      ) {
        pollTimer = setInterval(async () => {
          const status = await fetchAnalysisResults();
          if (status === 'completed' || status === 'error' || status === 'denied') {
            if (pollTimer) clearInterval(pollTimer);
          }
        }, 5000);
      }
    };

    startPolling();

    return () => {
      cancelled = true;
      if (pollTimer) clearInterval(pollTimer);
    };
  }, [analysisId, navigate]);

  useEffect(() => {
    if (!analysisId || !reportData?.isCompleted) {
      setMotionReviewNotes(null);
      setMotionReviewNotesLoading(false);
      return;
    }

    if (authLoading) {
      return;
    }

    let cancelled = false;
    const abortController = new AbortController();
    const showMotionNotesLoading = isMotionReviewNotesEnabled();

    if (showMotionNotesLoading) {
      setMotionReviewNotesLoading(true);
    }

    resolveReportMotionReviewNotes(analysisId, reportData.isCompleted, {
      signal: abortController.signal,
      onRetry: () => {
        if (!cancelled && showMotionNotesLoading) {
          setMotionReviewNotesLoading(true);
        }
      },
    })
      .then((payload) => {
        if (!cancelled) {
          setMotionReviewNotes(payload);
          setMotionReviewNotesLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setMotionReviewNotes(null);
          setMotionReviewNotesLoading(false);
        }
      });

    return () => {
      cancelled = true;
      abortController.abort();
      setMotionReviewNotesLoading(false);
    };
  }, [analysisId, reportData?.isCompleted, authLoading]);

  const handleCopyReportId = async () => {
    if (!analysisId) return;
    try {
      await navigator.clipboard.writeText(analysisId);
      setReportIdCopied(true);
      window.setTimeout(() => setReportIdCopied(false), 2000);
    } catch {
      window.prompt('Copy report ID:', analysisId);
    }
  };

  const handleShareReport = () => {
    if (navigator.share) {
      navigator.share({
        title: 'Scoutlytics Report',
        text: 'Check out this Madden scouting report!',
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Report URL copied to clipboard!');
    }
  };

  const toggleSectionExpand = (section: string) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const renderEvidenceTable = (
    sectionKey: string,
    title: string,
    entries: DetectionEntry[],
    columns: { key: keyof DetectionEntry | 'time'; label: string; render: (e: DetectionEntry) => string }[]
  ) => {
    if (!entries.length) return null;

    const expanded = expandedSections[sectionKey];
    const visible = expanded ? entries : entries.slice(0, EVIDENCE_PREVIEW_LIMIT);

    return (
      <div className="mb-6">
        <h4 className="font-medium text-secondary-900 dark:text-white mb-2">{title}</h4>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-secondary-500 border-b border-secondary-200 dark:border-secondary-700">
                {columns.map((col) => (
                  <th key={col.label} className="py-2 pr-4">
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visible.map((entry, idx) => (
                <tr key={idx} className="border-b border-secondary-100 dark:border-secondary-800">
                  {columns.map((col) => (
                    <td key={col.label} className="py-2 pr-4 text-secondary-700 dark:text-secondary-300">
                      {col.render(entry)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {entries.length > EVIDENCE_PREVIEW_LIMIT && (
          <button
            type="button"
            onClick={() => toggleSectionExpand(sectionKey)}
            className="mt-2 text-sm text-primary-600 hover:text-primary-700"
          >
            {expanded ? 'Show fewer' : `Show all ${entries.length} entries`}
          </button>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto">
        <div className="card text-center py-16 border-2 border-primary-400 dark:border-primary-400 bg-white dark:bg-gradient-to-br dark:from-primary-400/20 dark:to-secondary-900/80">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-secondary-900 dark:text-white mb-2">Loading Report</h2>
          <p className="text-secondary-600 dark:text-white">Preparing your scouting analysis...</p>
        </div>
      </div>
    );
  }

  if (error || !reportData) {
    const reportReturnTo = analysisId ? `/report/${analysisId}` : '/upload';
    return (
      <div className="max-w-6xl mx-auto">
        <div className="card text-center py-16 border-2 border-primary-400 dark:border-primary-400 bg-white dark:bg-gradient-to-br dark:from-primary-400/20 dark:to-secondary-900/80">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-secondary-900 dark:text-white mb-2">
            {accessState === 'auth'
              ? 'Sign in required'
              : accessState === 'forbidden'
                ? 'Access denied'
                : accessState === 'not_found'
                  ? 'Report not found'
                  : 'Report Error'}
          </h2>
          <p className="text-secondary-600 dark:text-white mb-4">{error || 'Failed to load report data'}</p>
          {accessState === 'auth' ? (
            <SignInWithTwitchButton returnTo={reportReturnTo} />
          ) : (
            <button onClick={() => navigate('/upload')} className="btn-primary">
              Start New Analysis
            </button>
          )}
        </div>
      </div>
    );
  }

  if ((reportData as any).status === 'error') {
    return (
      <div className="max-w-6xl mx-auto">
        <div className="card text-center py-16 border-2 border-primary-400 dark:border-primary-400 bg-white dark:bg-gradient-to-br dark:from-primary-400/20 dark:to-secondary-900/80">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-secondary-900 dark:text-white mb-2">Analysis Failed</h2>
          <p className="text-secondary-600 dark:text-white mb-4">
            {((reportData as any).error_message) ||
              reportData.scoutingSummary ||
              'An error occurred during analysis.'}
          </p>
          <button onClick={() => navigate('/upload')} className="btn-primary">
            Start New Analysis
          </button>
        </div>
      </div>
    );
  }

  const offense = reportData.teamScoutingReport?.offense;
  const offenseSnaps = getOffenseSnaps(offense);
  const runPassEvidence = hasRunPassEvidence(offense);
  const categoryConfidence =
    reportData.categoryConfidence ?? reportData.teamScoutingReport?.category_confidence ?? null;
  const confidenceTier =
    getConfidenceTierFromBackend(categoryConfidence) ??
    getConfidenceTier(reportData.stats.visionAnalyzedFrames, offenseSnaps);
  const bannerTier = resolveOverallConfidenceTier(categoryConfidence) ?? confidenceTier;
  const playsDetected = reportData.stats.visionDetections ?? 0;
  const hasEvidence =
    (reportData.detailedAnalysis?.formations_detected?.length ?? 0) > 0 ||
    (reportData.detailedAnalysis?.play_types?.length ?? 0) > 0 ||
    (reportData.detailedAnalysis?.down_distance_plays?.length ?? 0) > 0;

  const offenseTabLabel = offenseTabTitle(
    reportData.scoutingContext,
    reportData.teamScoutingReport?.team
  );
  const defenseTabLabel = defenseTabTitle(
    reportData.scoutingContext,
    reportData.teamScoutingReport?.team
  );

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-secondary-900 dark:text-white mb-2">Scouting Report</h1>
          <p className="text-secondary-600 dark:text-white">
            Scouting analysis from your gameplay footage
          </p>
          {analysisId && (
            <div className="flex flex-wrap items-center gap-2 mt-2">
              <span className="text-sm text-secondary-500 dark:text-primary-200">
                Report ID: <span className="font-mono text-xs">{analysisId}</span>
              </span>
              <button
                type="button"
                onClick={handleCopyReportId}
                className="inline-flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700 dark:text-primary-300"
              >
                {reportIdCopied ? (
                  <>
                    <Check className="h-3.5 w-3.5" />
                    Copied
                  </>
                ) : (
                  <>
                    <Copy className="h-3.5 w-3.5" />
                    Copy
                  </>
                )}
              </button>
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-3">
          <button onClick={handleShareReport} className="btn-secondary inline-flex items-center space-x-2">
            <Share2 className="h-4 w-4" />
            <span>Share</span>
          </button>
          <button
            type="button"
            disabled
            title="PDF export coming soon"
            className="btn-secondary inline-flex items-center space-x-2 opacity-60 cursor-not-allowed"
          >
            <Download className="h-4 w-4" />
            <span>PDF — Coming Soon</span>
          </button>
        </div>
      </div>

      {!reportData.isCompleted && progressStatus && progressStatus.status !== 'error' && (
        <div className="card mb-8 border-2 border-primary-200 dark:border-primary-700">
          <div className="flex items-center gap-3 mb-3">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600"></div>
            <p className="font-medium text-secondary-900 dark:text-white">Analysis in progress</p>
          </div>
          <div className="w-full bg-secondary-200 dark:bg-secondary-700 rounded-full h-2.5 mb-2">
            <div
              className="bg-primary-600 h-2.5 rounded-full transition-all duration-500"
              style={{ width: `${Math.min(100, Math.max(0, progressStatus.progress))}%` }}
            />
          </div>
          <p className="text-sm text-secondary-600 dark:text-secondary-300">
            {progressStatus.currentStep > 0 && `Step ${progressStatus.currentStep}: `}
            {progressStatus.message || 'Processing your video...'}
          </p>
          <p className="text-xs text-secondary-500 dark:text-secondary-400 mt-2">
            This page refreshes automatically every 5 seconds. You can leave and return later.
          </p>
        </div>
      )}

      {reportData.isCompleted && (
        <>
          <ScoutingContextBanner context={reportData.scoutingContext} />
          <ReportAnalysisQualityNotice analysisQuality={reportData.analysisQuality} />
          <ReportLimitedSampleNotice playsDetected={playsDetected} />
          <ReportConfidenceBanner
            tier={bannerTier}
            categoryConfidence={categoryConfidence}
            playsDetected={playsDetected}
          />
        </>
      )}

      {/* Tabs */}
      <div className="flex space-x-1 bg-white dark:bg-gradient-to-br dark:from-primary-400/20 dark:to-secondary-900/80 rounded-lg p-1 shadow-lg mb-8 border-2 border-primary-400 dark:border-primary-400">
        <button
          onClick={() => setActiveTab('summary')}
          className={`flex-1 px-4 py-2 rounded-md font-medium transition-colors ${
            activeTab === 'summary'
              ? 'bg-primary-600 text-white'
              : 'text-secondary-600 dark:text-white hover:text-secondary-900 dark:hover:text-primary-200'
          }`}
        >
          <FileText className="inline h-4 w-4 mr-2" />
          Summary
        </button>
        <button
          onClick={() => setActiveTab('offensive')}
          className={`flex-1 px-4 py-2 rounded-md font-medium transition-colors ${
            activeTab === 'offensive'
              ? 'bg-primary-600 text-white'
              : 'text-secondary-600 dark:text-white hover:text-secondary-900 dark:hover:text-primary-200'
          }`}
        >
          <Play className="inline h-4 w-4 mr-2" />
          {offenseTabLabel}
        </button>
        <button
          onClick={() => setActiveTab('defensive')}
          className={`flex-1 px-4 py-2 rounded-md font-medium transition-colors ${
            activeTab === 'defensive'
              ? 'bg-primary-600 text-white'
              : 'text-secondary-600 dark:text-white hover:text-secondary-900 dark:hover:text-primary-200'
          }`}
        >
          <Eye className="inline h-4 w-4 mr-2" />
          {defenseTabLabel}
        </button>
      </div>

      {/* Summary Tab */}
      {activeTab === 'summary' && (
        <div className="space-y-6">
          {hasRenderableScoutingReportV2(reportData.scoutingReportV2) ? (
            <CoachReportSection report={reportData.scoutingReportV2} />
          ) : null}
          <GameplanSnapshotSection snapshot={reportData.gameplanSnapshot} />
          <CoachLayerSection coachLayer={reportData.coachLayer} />

          <div className="card">
            <div className="prose prose-lg max-w-none">
              <div className="whitespace-pre-line text-secondary-700 dark:text-secondary-300 leading-relaxed">
                {reportData.scoutingSummary}
              </div>
            </div>
          </div>

          {motionReviewNotesLoading ? (
            <p
              className="text-xs text-secondary-500 dark:text-secondary-400"
              data-testid="motion-review-notes-loading"
            >
              Loading motion review notes…
            </p>
          ) : null}

          {hasRenderableMotionReviewNotes(motionReviewNotes) && motionReviewNotes ? (
            <MotionReviewNotesSection payload={motionReviewNotes} />
          ) : null}

          {/* C.2-5: Evidence panel */}
          {reportData.isCompleted && (
            <div className="card">
              <button
                type="button"
                onClick={() => setEvidenceOpen(!evidenceOpen)}
                className="w-full flex items-center justify-between text-left"
              >
                <h3 className="text-xl font-semibold text-secondary-900 dark:text-white">
                  Film Log (Sample)
                </h3>
                {evidenceOpen ? (
                  <ChevronUp className="h-5 w-5 text-secondary-500" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-secondary-500" />
                )}
              </button>

              {evidenceOpen && (
                <div className="mt-4">
                  {!hasEvidence ? (
                    <p className="text-secondary-600 dark:text-secondary-400 text-sm">
                      No timestamped plays are available for this report yet.
                    </p>
                  ) : (
                    <>
                      {renderEvidenceTable(
                        'formations',
                        'Formations',
                        reportData.detailedAnalysis?.formations_detected || [],
                        [
                          {
                            key: 'time',
                            label: 'Time',
                            render: (e) =>
                              e.timestamp != null ? formatTimestamp(e.timestamp) : '—',
                          },
                          {
                            key: 'formation',
                            label: 'Formation',
                            render: (e) => e.formation || '—',
                          },
                        ]
                      )}
                      {renderEvidenceTable(
                        'play_types',
                        'Play Types',
                        reportData.detailedAnalysis?.play_types || [],
                        [
                          {
                            key: 'time',
                            label: 'Time',
                            render: (e) =>
                              e.timestamp != null ? formatTimestamp(e.timestamp) : '—',
                          },
                          {
                            key: 'play_type',
                            label: 'Play Type',
                            render: (e) => e.play_type || '—',
                          },
                        ]
                      )}
                      {renderEvidenceTable(
                        'down_distance',
                        'Down & Distance',
                        reportData.detailedAnalysis?.down_distance_plays || [],
                        [
                          {
                            key: 'time',
                            label: 'Time',
                            render: (e) =>
                              e.timestamp != null ? formatTimestamp(e.timestamp) : '—',
                          },
                          {
                            key: 'down_distance',
                            label: 'Situation',
                            render: (e) => e.down_distance || '—',
                          },
                        ]
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Offensive Analysis Tab */}
      {activeTab === 'offensive' && (
        <div className="space-y-6">
          {reportData.teamScoutingReport?.offense ? (
            <>
              <div className="card">
                <h3 className="text-xl font-semibold text-secondary-900 dark:text-white mb-4">
                  {offenseTabLabel}
                </h3>
                <div className="prose prose-lg max-w-none">
                  <div className="whitespace-pre-line text-secondary-700 dark:text-secondary-300 leading-relaxed">
                    {reportData.teamScoutingReport.offense.summary ||
                      'No offensive analysis summary available.'}
                  </div>
                </div>
              </div>

              <div className="card">
                <h3 className="text-xl font-semibold text-secondary-900 dark:text-white mb-4">
                  Play Calling Identity
                </h3>
                {runPassEvidence ? (
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="bg-secondary-50 dark:bg-secondary-800 p-4 rounded-lg">
                      <div className="text-3xl font-bold text-primary-600 mb-2">
                        {(reportData.teamScoutingReport.offense.run_pass_ratio.pass * 100).toFixed(1)}%
                      </div>
                      <div className="text-sm text-secondary-500">Pass Rate</div>
                    </div>
                    <div className="bg-secondary-50 dark:bg-secondary-800 p-4 rounded-lg">
                      <div className="text-3xl font-bold text-primary-600 mb-2">
                        {(reportData.teamScoutingReport.offense.run_pass_ratio.run * 100).toFixed(1)}%
                      </div>
                      <div className="text-sm text-secondary-500">Run Rate</div>
                    </div>
                  </div>
                ) : (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/20 p-4">
                    <p className="font-medium text-secondary-900 dark:text-white">
                      Limited offensive sample
                    </p>
                    <p className="mt-2 text-sm text-secondary-600 dark:text-secondary-300">
                      Run/pass tendency needs a stronger sample of reviewed offensive plays. Check
                      back after more gameplay is captured, or use the film log to spot-check calls.
                    </p>
                  </div>
                )}
              </div>

              <div className="card">
                <h3 className="text-xl font-semibold text-secondary-900 dark:text-white mb-4">
                  Top Formations
                </h3>
                {reportData.teamScoutingReport.offense.top_formations.length > 0 ? (
                  <div className="grid md:grid-cols-3 gap-4">
                    {reportData.teamScoutingReport.offense.top_formations.map((formation, index) => (
                      <div
                        key={index}
                        className="bg-secondary-50 dark:bg-secondary-800 p-4 rounded-lg text-center"
                      >
                        <div className="text-lg font-semibold text-primary-600">
                          {formation.toUpperCase()}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-secondary-600 dark:text-secondary-400 text-sm">
                    Insufficient formation data for this upload.
                  </p>
                )}
              </div>

              {(() => {
                const offenseThirdDown = reportData.teamScoutingReport.offense.third_down;
                if (!offenseThirdDown) return null;
                const knownAttempts = thirdDownKnownAttempts(offenseThirdDown);
                const unknownAttempts = thirdDownUnknownAttempts(offenseThirdDown);
                const conversions = offenseThirdDown.conversions ?? 0;
                const sampleWarning = offenseThirdDown.result_inference?.sample_warning;
                if (knownAttempts <= 0 && unknownAttempts <= 0) return null;
                return (
                  <div className="card">
                    <h3 className="text-xl font-semibold text-secondary-900 dark:text-white mb-4">
                      Third Down (Offense)
                    </h3>
                    <div className="bg-secondary-50 dark:bg-secondary-800 p-4 rounded-lg">
                      {knownAttempts > 0 ? (
                        <>
                          <div className="text-3xl font-bold text-primary-600 mb-2">
                            {conversions}/{knownAttempts}
                          </div>
                          <div className="text-sm text-secondary-500">3rd Down Conversions</div>
                          {offenseThirdDown.conversion_rate != null && (
                            <div className="text-xs text-secondary-500 mt-1">
                              {(offenseThirdDown.conversion_rate * 100).toFixed(1)}% conversion rate
                            </div>
                          )}
                        </>
                      ) : (
                        <>
                          <div className="text-lg font-semibold text-secondary-700 dark:text-secondary-200 mb-2">
                            Third Down Conversions
                          </div>
                          <p className="text-sm text-secondary-600 dark:text-secondary-300">
                            Outcome unavailable — next reliable game state was not captured.
                          </p>
                        </>
                      )}
                      {unknownAttempts > 0 && (
                        <p className="text-xs text-amber-700 dark:text-amber-300 mt-2">
                          {unknownAttempts} outcome{unknownAttempts === 1 ? '' : 's'} unknown
                          {knownAttempts > 0 ? ' (excluded from rate)' : ''}
                        </p>
                      )}
                      {sampleWarning && (
                        <p className="text-xs text-secondary-500 mt-2">{sampleWarning}</p>
                      )}
                    </div>
                  </div>
                );
              })()}

              <div className="card">
                <h3 className="text-xl font-semibold text-secondary-900 dark:text-white mb-4">
                  Situational Playcalling
                </h3>
                <div className="space-y-4">
                  {Object.entries(reportData.teamScoutingReport.offense.situational.by_down).map(
                    ([down, plays]) => (
                      <div key={down} className="bg-secondary-50 dark:bg-secondary-800 p-4 rounded-lg">
                        <h4 className="font-medium text-secondary-900 dark:text-white mb-2">
                          {down} Down
                        </h4>
                        <div className="flex justify-between text-sm">
                          <span className="text-secondary-600 dark:text-secondary-300">
                            Pass-leaning: {plays.pass} plays
                          </span>
                          <span className="text-secondary-600 dark:text-secondary-300">
                            Run-leaning: {plays.run} plays
                          </span>
                        </div>
                      </div>
                    )
                  )}
                </div>
              </div>

              {(() => {
                const si = reportData.teamScoutingReport.offense.situational_intelligence;
                if (!si) return null;
                const third = si.third_down_analysis;
                const hasThird =
                  third && Object.keys(third).some((k) => (third[k]?.plays ?? 0) > 0);
                return (
                  <>
                    {si.most_predictable_situation && (
                      <div className="card border-primary-200 dark:border-primary-800">
                        <h3 className="text-xl font-semibold text-secondary-900 dark:text-white mb-4">
                          Most Predictable Situation
                        </h3>
                        <div className="bg-primary-50 dark:bg-primary-900/20 p-4 rounded-lg">
                          {si.most_predictable_situation.empty_state ? (
                            <p className="text-sm text-secondary-600 dark:text-secondary-300">
                              {si.most_predictable_situation.message ||
                                'No highly predictable situations identified.'}
                            </p>
                          ) : (
                            <>
                              <div className="text-lg font-semibold text-secondary-900 dark:text-white mb-2">
                                {si.most_predictable_situation.label}
                              </div>
                              <div className="grid grid-cols-2 gap-2 text-sm text-secondary-600 dark:text-secondary-300">
                                {si.most_predictable_situation.tendency === 'pass' && (
                                  <span>
                                    Pass rate: {si.most_predictable_situation.pass_rate}%
                                  </span>
                                )}
                                {si.most_predictable_situation.tendency === 'run' && (
                                  <span>Run rate: {si.most_predictable_situation.run_rate}%</span>
                                )}
                                <span>
                                  Predictability: {si.most_predictable_situation.predictability}
                                </span>
                                <span>Confidence: {si.most_predictable_situation.confidence}</span>
                                {si.most_predictable_situation.plays != null && (
                                  <span className="col-span-2">
                                    Observed across {si.most_predictable_situation.plays} snaps
                                  </span>
                                )}
                              </div>
                              {confidenceHelperLine(
                                si.most_predictable_situation.confidence,
                                si.most_predictable_situation.confidence_helper
                              ) && (
                                <p className="text-xs text-secondary-500 mt-2">
                                  {confidenceHelperLine(
                                    si.most_predictable_situation.confidence,
                                    si.most_predictable_situation.confidence_helper
                                  )}
                                </p>
                              )}
                              {si.most_predictable_situation.narrative && (
                                <p className="text-sm text-secondary-700 dark:text-secondary-300 mt-3">
                                  {si.most_predictable_situation.narrative}
                                </p>
                              )}
                              {si.most_predictable_situation.evidence &&
                                si.most_predictable_situation.evidence.length > 0 && (
                                  <p className="text-xs text-secondary-500 mt-2">
                                    Evidence:{' '}
                                    {si.most_predictable_situation.evidence.slice(0, 5).join(', ')}
                                  </p>
                                )}
                            </>
                          )}
                        </div>
                      </div>
                    )}
                    {si.predictability?.high_leverage_message && (
                      <div className="card">
                        <h3 className="text-lg font-semibold text-secondary-900 dark:text-white mb-2">
                          High-Leverage Situations
                        </h3>
                        <p className="text-sm text-secondary-600 dark:text-secondary-300">
                          {si.predictability.high_leverage_message}
                        </p>
                      </div>
                    )}
                    <div className="card">
                      <h3 className="text-xl font-semibold text-secondary-900 dark:text-white mb-4">
                        Offensive Insights
                      </h3>
                      {si.offensive_insights && si.offensive_insights.length > 0 ? (
                        <ul className="space-y-2">
                          {si.offensive_insights.map((line, idx) => (
                            <li
                              key={idx}
                              className="flex items-start text-secondary-700 dark:text-secondary-300 text-sm"
                            >
                              <span className="text-primary-500 mr-2">•</span>
                              {line}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-sm text-secondary-600 dark:text-secondary-300">
                          No high-confidence offensive tendencies identified in this sample.
                        </p>
                      )}
                    </div>
                    <div className="card">
                      <h3 className="text-xl font-semibold text-secondary-900 dark:text-white mb-4">
                        Defensive Insights
                      </h3>
                      {si.defensive_insights && si.defensive_insights.length > 0 ? (
                        <ul className="space-y-2">
                          {si.defensive_insights.map((line, idx) => (
                            <li
                              key={idx}
                              className="flex items-start text-secondary-700 dark:text-secondary-300 text-sm"
                            >
                              <span className="text-primary-500 mr-2">•</span>
                              {line}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-sm text-secondary-600 dark:text-secondary-300">
                          No high-confidence defensive tendencies identified in this sample.
                        </p>
                      )}
                    </div>
                    {hasThird && (
                      <div className="card">
                        <h3 className="text-xl font-semibold text-secondary-900 dark:text-white mb-4">
                          Third Down Analysis
                        </h3>
                        <div className="space-y-3">
                          {Object.entries(third).map(([key, bucket]) => (
                            <SituationBucketCard
                              key={key}
                              title={bucket.label ?? key.replace(/_/g, ' ')}
                              bucket={bucket}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                    {si.red_zone_analysis && (si.red_zone_analysis.plays ?? 0) > 0 && (
                      <div className="card">
                        <h3 className="text-xl font-semibold text-secondary-900 dark:text-white mb-4">
                          Red Zone Analysis
                        </h3>
                        <SituationBucketCard title="Red Zone" bucket={si.red_zone_analysis} />
                      </div>
                    )}
                    {si.two_minute_analysis && (si.two_minute_analysis.plays ?? 0) > 0 && (
                      <div className="card">
                        <h3 className="text-xl font-semibold text-secondary-900 dark:text-white mb-4">
                          Two-Minute Analysis
                        </h3>
                        <SituationBucketCard title="Two-Minute" bucket={si.two_minute_analysis} />
                      </div>
                    )}
                    {si.fourth_down_analysis &&
                      (si.fourth_down_analysis.attempts ?? 0) > 0 && (
                        <div className="card">
                          <h3 className="text-xl font-semibold text-secondary-900 dark:text-white mb-4">
                            Fourth Down Analysis
                          </h3>
                          <p className="text-sm text-secondary-600 dark:text-secondary-300 mb-3">
                            {si.fourth_down_analysis.attempts} attempt(s) in sample
                          </p>
                          {si.fourth_down_analysis.rollup && (
                            <SituationBucketCard
                              title="Fourth Down (all)"
                              bucket={si.fourth_down_analysis.rollup}
                            />
                          )}
                        </div>
                      )}
                  </>
                );
              })()}

              <div className="grid md:grid-cols-2 gap-6">
                {reportData.teamScoutingReport.offense.strengths?.length > 0 && (
                  <div className="card">
                    <h3 className="text-xl font-semibold text-secondary-900 dark:text-white mb-4">
                      Offensive Strengths
                    </h3>
                    <ul className="space-y-2">
                      {reportData.teamScoutingReport.offense.strengths.map((strength, index) => (
                        <li key={index} className="flex items-start">
                          <span className="text-green-500 mr-2">✓</span>
                          <span className="text-secondary-700 dark:text-secondary-300">{strength}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {reportData.teamScoutingReport.offense.weaknesses?.length > 0 && (
                  <div className="card">
                    <h3 className="text-xl font-semibold text-secondary-900 dark:text-white mb-4">
                      Areas for Improvement
                    </h3>
                    <ul className="space-y-2">
                      {reportData.teamScoutingReport.offense.weaknesses.map((weakness, index) => (
                        <li key={index} className="flex items-start">
                          <span className="text-yellow-500 mr-2">⚠</span>
                          <span className="text-secondary-700 dark:text-secondary-300">{weakness}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="card text-center py-8">
              <p className="text-secondary-600 dark:text-secondary-300">
                Offensive analysis data not available. Please check back later.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Defensive Analysis Tab */}
      {activeTab === 'defensive' && (
        <div className="space-y-6">
          {reportData.teamScoutingReport?.defense ? (
            <>
              <div className="card">
                <h3 className="text-xl font-semibold text-secondary-900 dark:text-white mb-4">
                  {defenseTabLabel}
                </h3>
                <div className="prose prose-lg max-w-none">
                  <div className="whitespace-pre-line text-secondary-700 dark:text-secondary-300 leading-relaxed">
                    {reportData.teamScoutingReport.defense.summary ||
                      'No defensive analysis summary available.'}
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="card">
                  <h3 className="text-xl font-semibold text-secondary-900 dark:text-white mb-4">
                    Top Coverages
                  </h3>
                  {reportData.teamScoutingReport.defense.top_coverages.length > 0 ? (
                    <div className="space-y-2">
                      {reportData.teamScoutingReport.defense.top_coverages.map((coverage, index) => (
                        <div
                          key={index}
                          className="bg-secondary-50 dark:bg-secondary-800 p-3 rounded-lg text-center"
                        >
                          <div className="text-lg font-semibold text-primary-600">
                            {coverage.toUpperCase()}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-secondary-600 dark:text-secondary-400 text-sm">
                      Insufficient coverage data for this upload.
                    </p>
                  )}
                </div>

                <div className="card">
                  <h3 className="text-xl font-semibold text-secondary-900 dark:text-white mb-4">
                    Top Fronts
                  </h3>
                  {reportData.teamScoutingReport.defense.top_fronts.length > 0 ? (
                    <div className="space-y-2">
                      {reportData.teamScoutingReport.defense.top_fronts.map((front, index) => (
                        <div
                          key={index}
                          className="bg-secondary-50 dark:bg-secondary-800 p-3 rounded-lg text-center"
                        >
                          <div className="text-lg font-semibold text-primary-600">
                            {front.toUpperCase()}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-secondary-600 dark:text-secondary-400 text-sm">
                      Insufficient front data for this upload.
                    </p>
                  )}
                </div>
              </div>

              {(() => {
                const cbs = reportData.teamScoutingReport.defense.coverage_by_situation;
                if (!cbs?.sections?.length) return null;
                const most = cbs.most_predictable_coverage_situation;
                return (
                  <>
                    {most && !most.insufficient_sample && (
                      <div className="card border-primary-200 dark:border-primary-800">
                        <h3 className="text-xl font-semibold text-secondary-900 dark:text-white mb-4">
                          Most Predictable Coverage Situation
                        </h3>
                        <div className="bg-primary-50 dark:bg-primary-900/20 p-4 rounded-lg">
                          <div className="text-lg font-semibold text-secondary-900 dark:text-white mb-2">
                            {most.label}
                          </div>
                          <p className="text-sm text-secondary-700 dark:text-secondary-300">
                            {most.narrative ||
                              `${most.top_coverage} (${most.share_percent}% of documented snaps, ${most.coverage_sample_size} observations)`}
                          </p>
                          <div className="grid grid-cols-2 gap-2 text-xs text-secondary-500 mt-2">
                            {most.coverage_predictability && (
                              <span>Predictability: {most.coverage_predictability}</span>
                            )}
                            {most.confidence && <span>Confidence: {most.confidence}</span>}
                          </div>
                          {confidenceHelperLine(most.confidence, most.confidence_helper) && (
                            <p className="text-xs text-secondary-500 mt-2">
                              {confidenceHelperLine(most.confidence, most.confidence_helper)}
                            </p>
                          )}
                          {most.evidence && most.evidence.length > 0 && (
                            <p className="text-xs text-secondary-500 mt-2">
                              Evidence: {most.evidence.slice(0, 5).join(', ')}
                            </p>
                          )}
                        </div>
                      </div>
                    )}

                    <div className="card">
                      <h3 className="text-xl font-semibold text-secondary-900 dark:text-white mb-4">
                        Coverage by Situation
                      </h3>
                      <div className="space-y-3">
                        {cbs.sections.map((section, idx) => (
                          <div
                            key={section.situation_key ?? idx}
                            className="bg-secondary-50 dark:bg-secondary-800 p-4 rounded-lg"
                          >
                            <h4 className="font-medium text-secondary-900 dark:text-white mb-1">
                              {section.label}
                            </h4>
                            {section.insufficient_sample ? (
                              <p className="text-sm text-secondary-600 dark:text-secondary-400">
                                {section.message || 'Insufficient coverage sample'}
                                {section.coverage_sample_size != null &&
                                  section.coverage_sample_size > 0 &&
                                  ` (${section.coverage_sample_size} reads)`}
                              </p>
                            ) : (
                              <>
                                <p className="text-sm text-secondary-700 dark:text-secondary-300">
                                  {section.top_coverage}
                                  {section.share_percent != null && ` (${section.share_percent}%)`}
                                  {section.coverage_sample_size != null &&
                                    ` · ${section.coverage_sample_size} observations`}
                                </p>
                                {section.coverage_predictability && (
                                  <p className="text-xs text-secondary-500 mt-1">
                                    Predictability: {section.coverage_predictability}
                                    {section.confidence && ` · Confidence: ${section.confidence}`}
                                  </p>
                                )}
                                {section.evidence && section.evidence.length > 0 && (
                                  <p className="text-xs text-secondary-500 mt-1">
                                    Evidence: {section.evidence.slice(0, 5).join(', ')}
                                  </p>
                                )}
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>

                    {cbs.coaching_insights && cbs.coaching_insights.length > 0 && (
                      <div className="card">
                        <h3 className="text-xl font-semibold text-secondary-900 dark:text-white mb-4">
                          Coverage Coaching Insights
                        </h3>
                        <ul className="space-y-2">
                          {cbs.coaching_insights.map((line, idx) => (
                            <li
                              key={idx}
                              className="flex items-start text-secondary-700 dark:text-secondary-300 text-sm"
                            >
                              <span className="text-primary-500 mr-2">•</span>
                              {line}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </>
                );
              })()}

              <div className="card">
                <h3 className="text-xl font-semibold text-secondary-900 dark:text-white mb-4">
                  Pressure Statistics
                </h3>
                <div className="grid md:grid-cols-3 gap-6">
                  <div className="bg-secondary-50 dark:bg-secondary-800 p-4 rounded-lg text-center">
                    <div className="text-3xl font-bold text-primary-600 mb-2">
                      {(reportData.teamScoutingReport.defense.blitz_rate * 100).toFixed(1)}%
                    </div>
                    <div className="text-sm text-secondary-500">Blitz Rate</div>
                  </div>
                  {reportData.teamScoutingReport.defense.pressure_profile_reliable !== false &&
                  reportData.teamScoutingReport.defense.avg_rushers != null ? (
                    <div className="bg-secondary-50 dark:bg-secondary-800 p-4 rounded-lg text-center">
                      <div className="text-3xl font-bold text-primary-600 mb-2">
                        {reportData.teamScoutingReport.defense.avg_rushers.toFixed(1)}
                      </div>
                      <div className="text-sm text-secondary-500">Avg Rushers</div>
                    </div>
                  ) : (
                    <div className="bg-secondary-50 dark:bg-secondary-800 p-4 rounded-lg text-center">
                      <p className="text-sm text-secondary-600 dark:text-secondary-300">
                        Pressure profile not reliably classified from this sample.
                      </p>
                    </div>
                  )}
                  {(() => {
                    const defenseThirdDown = reportData.teamScoutingReport.defense.third_down;
                    if (!defenseThirdDown) return null;
                    const knownAttempts = thirdDownKnownAttempts(defenseThirdDown);
                    const unknownAttempts = thirdDownUnknownAttempts(defenseThirdDown);
                    const stops = defenseThirdDown.stops ?? 0;
                    const sampleWarning = defenseThirdDown.result_inference?.sample_warning;
                    if (knownAttempts <= 0 && unknownAttempts <= 0) return null;
                    return (
                      <div className="bg-secondary-50 dark:bg-secondary-800 p-4 rounded-lg text-center">
                        {knownAttempts > 0 ? (
                          <>
                            <div className="text-3xl font-bold text-primary-600 mb-2">
                              {stops}/{knownAttempts}
                            </div>
                            <div className="text-sm text-secondary-500">3rd Down Stops</div>
                            {defenseThirdDown.stop_rate != null && stops > 0 && (
                              <div className="text-xs text-secondary-500 mt-1">
                                {(defenseThirdDown.stop_rate * 100).toFixed(1)}% stop rate
                              </div>
                            )}
                          </>
                        ) : (
                          <>
                            <div className="text-lg font-semibold text-secondary-700 dark:text-secondary-200 mb-2">
                              3rd Down Stops
                            </div>
                            <p className="text-sm text-secondary-600 dark:text-secondary-300">
                              Outcome unavailable — next reliable game state was not captured.
                            </p>
                          </>
                        )}
                        {unknownAttempts > 0 && (
                          <p className="text-xs text-amber-700 dark:text-amber-300 mt-2">
                            {unknownAttempts} outcome{unknownAttempts === 1 ? '' : 's'} unknown
                          </p>
                        )}
                        {sampleWarning && (
                          <p className="text-xs text-secondary-500 mt-2">{sampleWarning}</p>
                        )}
                      </div>
                    );
                  })()}
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {reportData.teamScoutingReport.defense.strengths?.length > 0 && (
                  <div className="card">
                    <h3 className="text-xl font-semibold text-secondary-900 dark:text-white mb-4">
                      Defensive Strengths
                    </h3>
                    <ul className="space-y-2">
                      {reportData.teamScoutingReport.defense.strengths.map((strength, index) => (
                        <li key={index} className="flex items-start">
                          <span className="text-green-500 mr-2">✓</span>
                          <span className="text-secondary-700 dark:text-secondary-300">{strength}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {reportData.teamScoutingReport.defense.weaknesses?.length > 0 && (
                  <div className="card">
                    <h3 className="text-xl font-semibold text-secondary-900 dark:text-white mb-4">
                      Areas for Improvement
                    </h3>
                    <ul className="space-y-2">
                      {reportData.teamScoutingReport.defense.weaknesses.map((weakness, index) => (
                        <li key={index} className="flex items-start">
                          <span className="text-yellow-500 mr-2">⚠</span>
                          <span className="text-secondary-700 dark:text-secondary-300">{weakness}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="card text-center py-8">
              <p className="text-secondary-600 dark:text-secondary-300">
                Defensive analysis data not available. Please check back later.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Action Buttons */}
      <div className="mt-8 text-center">
        <button
          onClick={() => navigate('/upload')}
          className="btn-primary inline-flex items-center space-x-2"
        >
          <FileText className="h-4 w-4" />
          <span>Generate New Report</span>
        </button>
      </div>
    </div>
  );
};

export default Report;
